/**
 * buchhaltungApi — Fibu (SKR04) işlemleri için tek API katmanı.
 *
 * Kurallar:
 *  - Buchung YALNIZCA fibu_post_entry RPC ile atılır (Soll=Haben +
 *    periode kilidi DB tarafında atomik doğrulanır). Kayıt silinmez;
 *    düzeltme fibu_reverse_entry (Storno) ile yapılır — GoBD uyumlu.
 *  - Raporlar (SuSa, GuV, Bilanz) konten-METADATA üzerinden üretilir;
 *    hesap numaraları koda gömülmez.
 *  - Fatura köprüsü idempotenttir: aynı sipariş iki kez buchulamaz
 *    (fibu_documents.external_reference UNIQUE).
 */
import { SupabaseAdapter } from '@/api/adapters/SupabaseAdapter';
import { getOrderItems, getOrderTotal } from '@/lib/orderFields';
import { computeInvoiceTotals } from '@/lib/invoiceCalc';
import { issueInvoiceFromOrder, recordPaymentRpc } from '@/lib/invoiceApi';

export const COMPANY_ID = 1; // InMotion — SaaS'ta mandant seçiminden gelir

function getClient() {
  try { return SupabaseAdapter.getClient(); } catch { return null; }
}

function requireClient() {
  const client = getClient();
  if (!client) throw new Error('Supabase Verbindung nicht verfügbar.');
  return client;
}

/** Fibu hata mesajlarını kullanıcı diline çevirir. */
export function fibuErrorToGerman(err) {
  const msg = err?.message || String(err);
  if (msg.includes('fibu_forbidden')) return 'Keine Buchhaltungs-Berechtigung.';
  if (msg.includes('fibu_period_locked')) return 'Periode/Jahr ist gesperrt — nur Storno möglich.';
  if (msg.includes('fibu_unbalanced')) return 'Buchung nicht ausgeglichen: Soll muss gleich Haben sein.';
  if (msg.includes('fibu_already_reversed')) return 'Buchung wurde bereits storniert.';
  if (msg.includes('duplicate key') && msg.includes('external_reference')) {
    return 'Beleg wurde bereits gebucht (Duplikat verhindert).';
  }
  return msg;
}

// ── Stammdaten ────────────────────────────────────────────────

export async function listAccounts({ activeOnly = true } = {}) {
  const client = requireClient();
  let q = client.from('fibu_accounts').select('*').order('account_no');
  if (activeOnly) q = q.eq('active', true);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function listTaxCodes() {
  const client = requireClient();
  const { data, error } = await client.from('fibu_tax_codes').select('*').eq('active', true).order('code');
  if (error) throw error;
  return data || [];
}

// ── Journal ───────────────────────────────────────────────────

/**
 * Buchungssatz kaydeder (RPC — atomik, Soll=Haben garantili).
 * @param {{ bookingDate: string, description: string, source?: string,
 *   sourceId?: string, lines: Array<{account_no, debit, credit, tax_code?, description?}>,
 *   actorEmail?: string, documentId?: number }} args
 * @returns {Promise<number>} journal_entry id
 */
export async function postEntry({ bookingDate, description, source = 'manual', sourceId = null, lines, actorEmail = null, documentId = null }) {
  const client = requireClient();
  const { data, error } = await client.rpc('fibu_post_entry', {
    p_company_id: COMPANY_ID,
    p_booking_date: bookingDate,
    p_description: description,
    p_source: source,
    p_source_id: sourceId,
    p_lines: lines,
    p_actor: actorEmail,
    p_document_id: documentId,
  });
  if (error) throw new Error(fibuErrorToGerman(error));
  return data;
}

export async function reverseEntry(entryId, actorEmail = null) {
  const client = requireClient();
  const { data, error } = await client.rpc('fibu_reverse_entry', {
    p_entry_id: entryId,
    p_actor: actorEmail,
  });
  if (error) throw new Error(fibuErrorToGerman(error));
  return data;
}

export async function listJournalEntries({ year = null, limit = 200 } = {}) {
  const client = requireClient();
  let q = client
    .from('fibu_journal_entries')
    .select('*, fibu_journal_lines(*)')
    .eq('company_id', COMPANY_ID)
    .order('booking_date', { ascending: false })
    .order('id', { ascending: false })
    .limit(limit);
  if (year) {
    q = q.gte('booking_date', `${year}-01-01`).lte('booking_date', `${year}-12-31`);
  }
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

// ── Raporlar (metadata-driven) ────────────────────────────────

/**
 * Dönem içi hesap salt hareketlerini toplar → SuSa/GuV/Bilanz temelidir.
 * @returns {Promise<Map<string, {debit:number, credit:number}>>} account_no → toplamlar
 */
export async function fetchAccountTotals({ dateFrom, dateTo }) {
  const client = requireClient();
  const { data, error } = await client
    .from('fibu_journal_lines')
    .select('account_no, debit, credit, fibu_journal_entries!inner(booking_date, status, company_id)')
    .eq('fibu_journal_entries.company_id', COMPANY_ID)
    .neq('fibu_journal_entries.status', 'draft')
    .gte('fibu_journal_entries.booking_date', dateFrom)
    .lte('fibu_journal_entries.booking_date', dateTo);
  if (error) throw error;

  const totals = new Map();
  (data || []).forEach((l) => {
    const t = totals.get(l.account_no) || { debit: 0, credit: 0 };
    t.debit += Number(l.debit) || 0;
    t.credit += Number(l.credit) || 0;
    totals.set(l.account_no, t);
  });
  return totals;
}

/** Konto saldosu — normal_side'a göre işaretli. */
export function accountBalance(account, totals) {
  const t = totals.get(account.account_no) || { debit: 0, credit: 0 };
  return account.normal_side === 'debit' ? t.debit - t.credit : t.credit - t.debit;
}

/** GuV: guv_position gruplarına göre (metadata-driven). */
export function buildGuV(accounts, totals) {
  const groups = new Map();
  let revenue = 0, expense = 0;
  accounts
    .filter((a) => a.statement_type === 'guv' || a.statement_type === 'both')
    .forEach((a) => {
      const bal = accountBalance(a, totals);
      if (bal === 0) return;
      const pos = a.guv_position || 'Sonstige';
      const g = groups.get(pos) || { position: pos, amount: 0, type: a.account_type };
      // revenue pozitif katkı, expense negatif — Ergebnis = revenue - expense
      const signed = a.account_type === 'revenue' ? bal : -bal;
      g.amount += signed;
      groups.set(pos, g);
      if (a.account_type === 'revenue') revenue += bal; else expense += bal;
    });
  return {
    groups: [...groups.values()].sort((x, y) => y.amount - x.amount),
    revenue, expense, result: revenue - expense,
  };
}

/** Bilanz: balance_position gruplarına göre Aktiva/Passiva. */
export function buildBilanz(accounts, totals, guvResult = 0) {
  const aktiva = new Map(); const passiva = new Map();
  accounts
    .filter((a) => a.statement_type === 'balance' || a.statement_type === 'both')
    .forEach((a) => {
      const bal = accountBalance(a, totals);
      if (bal === 0) return;
      const side = a.account_type === 'asset' ? aktiva : passiva;
      const pos = a.balance_position || 'Sonstige';
      const g = side.get(pos) || { position: pos, amount: 0 };
      g.amount += bal;
      side.set(pos, g);
    });
  // Jahresergebnis Passiva'ya (Eigenkapital) eklenir — Bilanz dengelenir
  if (guvResult !== 0) {
    const g = passiva.get('Jahresüberschuss/-fehlbetrag') || { position: 'Jahresüberschuss/-fehlbetrag', amount: 0 };
    g.amount += guvResult;
    passiva.set('Jahresüberschuss/-fehlbetrag', g);
  }
  const sum = (m) => [...m.values()].reduce((s, g) => s + g.amount, 0);
  return {
    aktiva: [...aktiva.values()].sort((x, y) => y.amount - x.amount),
    passiva: [...passiva.values()].sort((x, y) => y.amount - x.amount),
    sumAktiva: sum(aktiva), sumPassiva: sum(passiva),
  };
}

// ── OPOS ──────────────────────────────────────────────────────

export async function listOpenItems({ type = null } = {}) {
  const client = requireClient();
  let q = client.from('fibu_open_items').select('*')
    .eq('company_id', COMPANY_ID)
    .in('status', ['open', 'partial'])
    .order('due_date', { ascending: true });
  if (type) q = q.eq('type', type);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

/**
 * Zahlung buchen. Neu: eine atomare DB-Funktion (Buchung + Zahlungszeile +
 * offener Betrag + Rechnungsstatus gemeinsam). Fehlt die Migration noch,
 * läuft der bisherige Weg weiter.
 */
export async function recordPayment(args) {
  try {
    return await recordPaymentRpc({
      openItemId: args.openItem.id,
      amount: args.amount,
      paymentDate: args.paymentDate,
      bankAccountNo: args.bankAccountNo || '1800',
      actorEmail: args.actorEmail,
      transactionId: args.transactionId,
    });
  } catch (err) {
    if (err.code !== 'RPC_FEHLT') throw err;
    return legacyRecordPayment(args);
  }
}

async function legacyRecordPayment({ openItem, amount, paymentDate, bankAccountNo = '1800', actorEmail }) {
  const client = requireClient();
  const isReceivable = openItem.type === 'receivable';
  const lines = isReceivable
    ? [
        { account_no: bankAccountNo, debit: amount, credit: 0, description: `Zahlung ${openItem.invoice_no}` },
        { account_no: '1200', debit: 0, credit: amount, description: `Ausgleich ${openItem.invoice_no}` },
      ]
    : [
        { account_no: '3300', debit: amount, credit: 0, description: `Zahlung ${openItem.invoice_no}` },
        { account_no: bankAccountNo, debit: 0, credit: amount, description: `Ausgleich ${openItem.invoice_no}` },
      ];
  const entryId = await postEntry({
    bookingDate: paymentDate,
    description: `Zahlung ${openItem.invoice_no} — ${openItem.partner_name || ''}`,
    source: 'bank',
    sourceId: `payment:${openItem.id}`,
    lines,
    actorEmail,
  });

  const newOpen = Math.max(0, Number(openItem.open_amount) - amount);
  const { error: e1 } = await client.from('fibu_open_item_payments').insert({
    open_item_id: openItem.id, journal_entry_id: entryId,
    payment_date: paymentDate, amount,
  });
  if (e1) throw e1;
  const { error: e2 } = await client.from('fibu_open_items').update({
    open_amount: newOpen,
    status: newOpen === 0 ? 'paid' : 'partial',
    last_payment_date: paymentDate,
  }).eq('id', openItem.id);
  if (e2) throw e2;
  return entryId;
}

// ── Fatura köprüsü (ERP → Fibu, idempotent) ───────────────────

/**
 * Faturalanmış ERP siparişlerinden henüz buchulmamış olanları döner.
 * Idempotens: fibu_documents.external_reference = 'order:<id>'.
 */
export async function findUnbookedInvoices(orders) {
  const client = requireClient();
  const invoiced = orders.filter((o) => o.invoice_number && o.status !== 'Storniert');
  if (invoiced.length === 0) return [];
  const refs = invoiced.map((o) => `order:${o.id}`);
  const { data, error } = await client
    .from('fibu_documents')
    .select('external_reference')
    .eq('company_id', COMPANY_ID)
    .in('external_reference', refs);
  if (error) throw error;
  const booked = new Set((data || []).map((d) => d.external_reference));
  return invoiced.filter((o) => !booked.has(`order:${o.id}`));
}

/**
 * Tek bir ERP faturasını Fibu'ya buchar:
 *   Debitor (1200) an Erlöse (4400) + USt (3806), OPOS açılır.
 * Idempotent: aynı sipariş ikinci kez buchulamaz (UNIQUE).
 */
export async function bookInvoiceFromOrder(order, actorEmail) {
  // Neu: Rechnung atomar über die DB (idempotent — vorhandene Rechnung wird zurückgegeben)
  try {
    const inv = await issueInvoiceFromOrder(order.id, actorEmail);
    return inv?.journal_entry_id ?? null;
  } catch (err) {
    if (err.code !== 'RPC_FEHLT') throw err;
  }
  return legacyBookInvoiceFromOrder(order, actorEmail);
}

async function legacyBookInvoiceFromOrder(order, actorEmail) {
  const client = requireClient();
  // Betrag exakt wie auf der Rechnung (inkl. Kundenrabatt) — vorher wurde
  // der Bestellwert ohne Kundenrabatt gebucht.
  let kundenrabattProzent = 0;
  if (order.lead_id) {
    const { data: lead } = await client.from('leads').select('kundenrabatt_prozent').eq('id', order.lead_id).maybeSingle();
    kundenrabattProzent = Number(lead?.kundenrabatt_prozent) || 0;
  }
  const items = getOrderItems(order);
  const calc = computeInvoiceTotals(items, { taxRate: Number(order.tax_rate ?? 19), kundenrabattProzent });
  const useCalc = items.length > 0 && calc.total > 0;
  const gross = useCalc ? calc.total : (getOrderTotal(order) || 0);
  const net = useCalc ? calc.netto : (Number(order.subtotal) || +(gross / 1.19).toFixed(2));
  const tax = useCalc ? calc.taxAmount : (Number(order.tax_amount) || +(gross - net).toFixed(2));
  if (gross <= 0) throw new Error(`Rechnung ${order.invoice_number}: Betrag 0 — übersprungen.`);

  const invoiceDate = (order.invoiced_at || order.updated_date || new Date().toISOString()).slice(0, 10);

  // 1) Beleg (UNIQUE external_reference → duplikat DB'de engellenir)
  const { data: doc, error: docErr } = await client.from('fibu_documents').insert({
    company_id: COMPANY_ID,
    document_no: order.invoice_number,
    document_type: 'invoice_out',
    document_date: invoiceDate,
    customer_name: order.lead_name || '',
    net_amount: net, tax_amount: tax, gross_amount: gross,
    external_reference: `order:${order.id}`,
  }).select().single();
  if (docErr) throw new Error(fibuErrorToGerman(docErr));

  // 2) Buchung: 1200 an 4400 + 3806
  const lines = [
    { account_no: '1200', debit: gross, credit: 0, description: `RE ${order.invoice_number} ${order.lead_name || ''}` },
    { account_no: '4400', debit: 0, credit: net, tax_code: 'USt19', description: 'Erlöse 19%' },
  ];
  if (tax > 0) {
    lines.push({ account_no: '3806', debit: 0, credit: tax, description: 'USt 19%' });
  }
  const entryId = await postEntry({
    bookingDate: invoiceDate,
    description: `Ausgangsrechnung ${order.invoice_number} — ${order.lead_name || ''}`,
    source: 'invoice',
    sourceId: `order:${order.id}`,
    lines,
    actorEmail,
    documentId: doc.id,
  });

  // 3) OPOS
  const due = new Date(invoiceDate);
  due.setDate(due.getDate() + 14);
  const { error: oiErr } = await client.from('fibu_open_items').insert({
    company_id: COMPANY_ID,
    type: 'receivable',
    partner_name: order.lead_name || '',
    partner_email: order.lead_email || null,
    document_id: doc.id,
    invoice_no: order.invoice_number,
    invoice_date: invoiceDate,
    due_date: due.toISOString().slice(0, 10),
    original_amount: gross,
    open_amount: gross,
  });
  if (oiErr) throw oiErr;

  return entryId;
}

export const fmtEUR = (v) =>
  Number(v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

// ════════════════════════════════════════════════════════════
// FAZ 2 — Finanz-API, Eşleştirme, AfA, UStVA, Mahnwesen
// ════════════════════════════════════════════════════════════

// ── Finanzverbindungen (Bank / PayPal / eBay / Kasse) ─────────

export async function listConnections() {
  const client = requireClient();
  const { data, error } = await client.from('fibu_financial_connections')
    .select('*').eq('company_id', COMPANY_ID).order('id');
  if (error) throw error;
  return data || [];
}

export async function createConnection({ provider, displayName, iban = null, linkedAccountNo }) {
  const client = requireClient();
  const { data, error } = await client.from('fibu_financial_connections').insert({
    company_id: COMPANY_ID, provider, display_name: displayName,
    iban, linked_account_no: linkedAccountNo,
  }).select().single();
  if (error) throw error;
  return data;
}

/**
 * CSV satırlarını normalize edip içe aktarır. Dedupe: external id yoksa
 * tarih+tutar+amaç hash'i kullanılır; UNIQUE kısıt duplikatı DB'de engeller.
 * @returns {{imported:number, skipped:number}}
 */
export async function importTransactions(connectionId, rows) {
  const client = requireClient();
  let imported = 0, skipped = 0;
  // Ohne Bank-ID: Schlüssel aus Datum|Betrag|Zweck. Identische Buchungen am
  // selben Tag bekommen eine laufende Nummer (#2, #3 …), damit zwei echte
  // 50-€-Zahlungen nicht zu einer verschmelzen. Die erste behält das alte
  // Format → erneuter Import alter Dateien bleibt dupliziersicher.
  const seen = new Map();
  for (const r of rows) {
    const baseKey = `${r.booking_date}|${r.amount}|${(r.purpose || '').slice(0, 60)}`;
    const n = (seen.get(baseKey) || 0) + 1;
    seen.set(baseKey, n);
    const extId = r.external_transaction_id || (n === 1 ? baseKey : `${baseKey}|#${n}`);
    const { error } = await client.from('fibu_financial_transactions').insert({
      connection_id: connectionId,
      external_transaction_id: extId,
      booking_date: r.booking_date,
      amount: r.amount,
      fees: r.fees || 0,
      counterparty_name: r.counterparty_name || null,
      purpose: r.purpose || null,
    });
    if (error) {
      if ((error.message || '').includes('duplicate')) { skipped++; continue; }
      throw error;
    }
    imported++;
  }
  return { imported, skipped };
}

export async function listUnmatchedTransactions() {
  const client = requireClient();
  const { data, error } = await client.from('fibu_financial_transactions')
    .select('*, fibu_financial_connections!inner(company_id, display_name, provider, linked_account_no)')
    .eq('fibu_financial_connections.company_id', COMPANY_ID)
    .eq('reconciliation_status', 'unmatched')
    .order('booking_date', { ascending: false })
    .limit(200);
  if (error) throw error;
  return data || [];
}

/**
 * Otomatik eşleştirme önerisi: tutar birebir + (fatura no amaçta geçiyor
 * veya tek aday). Kesin eşleşme yoksa null.
 */
// Richtungsgerechter Abgleich (Ausgang → Kreditor, Eingang → Debitor)
export { suggestMatch, itemsForTransaction } from '@/lib/bankMatching';

/** Eşleştir: ödeme buchung'u + OPOS güncelle + transaksiyonu bağla. */
export async function matchTransaction(tx, openItem, actorEmail) {
  const client = requireClient();
  // Überzahlung bei Kundenrechnungen nicht stillschweigend abschneiden —
  // der Rest ginge sonst in der Buchhaltung verloren.
  if (openItem.type !== 'payable'
    && Math.abs(Number(tx.amount)) > Number(openItem.open_amount) + 0.005) {
    throw new Error(`Zahlung (${fmtEUR(Math.abs(Number(tx.amount)))}) ist höher als der offene Betrag (${fmtEUR(openItem.open_amount)}). Bitte im Abgleich aufteilen.`);
  }
  // Atomar reservieren: Auto-Abgleich und manueller Klick dürfen denselben
  // Umsatz nie zweimal buchen. Nur wer 'unmatched' → 'matched' umstellt, bucht.
  const { data: claimed, error: claimErr } = await client.from('fibu_financial_transactions')
    .update({ reconciliation_status: 'matched', matched_open_item_id: openItem.id })
    .eq('id', tx.id).eq('reconciliation_status', 'unmatched').select('id');
  if (claimErr) throw claimErr;
  if (!claimed?.length) throw new Error('Dieser Umsatz wurde bereits zugeordnet.');

  const bankAccountNo = tx.fibu_financial_connections?.linked_account_no || '1800';
  const amount = Math.min(Math.abs(Number(tx.amount)), Number(openItem.open_amount));
  let entryId;
  try {
    entryId = await recordPayment({
      openItem, amount, transactionId: tx.id,
      paymentDate: tx.booking_date || new Date().toISOString().slice(0, 10),
      bankAccountNo, actorEmail,
    });
  } catch (err) {
    await client.from('fibu_financial_transactions')
      .update({ reconciliation_status: 'unmatched', matched_open_item_id: null }).eq('id', tx.id);
    throw err;
  }
  const { error } = await client.from('fibu_financial_transactions')
    .update({ matched_journal_entry_id: entryId }).eq('id', tx.id);
  if (error) throw error;
  return entryId;
}

export async function ignoreTransaction(txId) {
  const client = requireClient();
  const { error } = await client.from('fibu_financial_transactions')
    .update({ reconciliation_status: 'ignored' }).eq('id', txId);
  if (error) throw error;
}

// ── Anlagevermögen + AfA ─────────────────────────────────────

export async function listFixedAssets() {
  const client = requireClient();
  const { data, error } = await client.from('fibu_fixed_assets')
    .select('*').eq('company_id', COMPANY_ID).order('asset_no');
  if (error) throw error;
  return data || [];
}

export async function createFixedAsset(asset) {
  const client = requireClient();
  const { data, error } = await client.from('fibu_fixed_assets').insert({
    company_id: COMPANY_ID, ...asset,
  }).select().single();
  if (error) throw error;
  return data;
}

/** Aktifleştirme ayında pro-rata dahil, yıl için lineer AfA tutarı. */
export function yearlyDepreciation(asset, year) {
  const life = Number(asset.useful_life_years) || 0;
  if (life <= 0 || asset.status !== 'active') return 0;
  const cost = Number(asset.acquisition_cost) - Number(asset.residual_value || 0);
  const annual = cost / life;
  const act = new Date(asset.activation_date);
  if (act.getFullYear() > year) return 0;
  // İlk yıl: aktifleştirme ayından itibaren (ay bazlı pro-rata, §7 EStG)
  const months = act.getFullYear() === year ? 12 - act.getMonth() : 12;
  const remaining = cost - Number(asset.accumulated_depreciation || 0);
  if (remaining <= 0.005) return 0;
  return Math.min(+(annual * months / 12).toFixed(2), +remaining.toFixed(2));
}

/**
 * AfA-Lauf (yıl): her aktif kıymet için lineer AfA hesaplar,
 * TEK buchung atar (AfA-Konto Soll an Anlagenkonto Haben, direkte Methode)
 * ve kümülatif AfA'yı günceller. (fiscal_year, period=12) UNIQUE →
 * aynı yıl için ikinci kez çalıştırılamaz.
 */
export async function runDepreciation(year, actorEmail) {
  const client = requireClient();
  const assets = await listFixedAssets();
  const items = assets
    .map((a) => ({ asset: a, amount: yearlyDepreciation(a, year) }))
    .filter((x) => x.amount > 0);
  if (items.length === 0) throw new Error(`Keine AfA-Beträge für ${year}.`);

  const lines = [];
  items.forEach(({ asset, amount }) => {
    lines.push({
      account_no: asset.depreciation_account_no || '6220',
      debit: amount, credit: 0,
      description: `AfA ${asset.asset_no} ${asset.name}`,
    });
    lines.push({
      account_no: asset.asset_account_no,
      debit: 0, credit: amount,
      description: `AfA ${asset.asset_no}`,
    });
  });

  const entryId = await postEntry({
    bookingDate: `${year}-12-31`,
    description: `AfA-Lauf ${year} (${items.length} Anlagegüter)`,
    source: 'asset',
    sourceId: `afa:${year}`,
    lines,
    actorEmail,
  });

  // Run kaydı (UNIQUE (company, fy, period) → tekrar koruması)
  const { data: fy } = await client.from('fibu_fiscal_years')
    .select('id').eq('company_id', COMPANY_ID).eq('year', year).single();
  const { error: runErr } = await client.from('fibu_depreciation_runs').insert({
    company_id: COMPANY_ID, fiscal_year_id: fy.id, period: 12,
    run_date: new Date().toISOString().slice(0, 10),
    status: 'posted', journal_entry_id: entryId,
  });
  if (runErr) {
    if ((runErr.message || '').includes('duplicate')) {
      throw new Error(`AfA-Lauf ${year} wurde bereits gebucht (Buchung ${entryId} bitte stornieren).`);
    }
    throw runErr;
  }

  for (const { asset, amount } of items) {
    await client.from('fibu_fixed_assets').update({
      accumulated_depreciation: +(Number(asset.accumulated_depreciation || 0) + amount).toFixed(2),
    }).eq('id', asset.id);
  }
  return { entryId, count: items.length, total: items.reduce((s, x) => s + x.amount, 0) };
}

// ── UStVA ─────────────────────────────────────────────────────

/**
 * UStVA hesaplar — steuer hesapları fibu_tax_codes METADATA'sından
 * (output/input tax account) toplanır; kod içine hesap gömülü değildir.
 */
export async function computeUStVA({ year, month }) {
  const dateFrom = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const dateTo = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
  const [totals, taxCodes] = await Promise.all([
    fetchAccountTotals({ dateFrom, dateTo }),
    listTaxCodes(),
  ]);

  const sumFor = (accountNo, side) => {
    if (!accountNo) return 0;
    const t = totals.get(accountNo) || { debit: 0, credit: 0 };
    return side === 'credit' ? t.credit - t.debit : t.debit - t.credit;
  };

  const rows = [];
  let outputTax = 0, inputTax = 0;
  taxCodes.forEach((tc) => {
    const out = sumFor(tc.output_tax_account_no, 'credit');
    const inp = sumFor(tc.input_tax_account_no, 'debit');
    if (out !== 0) { rows.push({ code: tc.code, label: tc.name, kind: 'USt', field: tc.ustva_tax_field, amount: out }); outputTax += out; }
    if (inp !== 0) { rows.push({ code: tc.code, label: tc.name, kind: 'VSt', field: '66', amount: inp }); inputTax += inp; }
  });

  return { year, month, dateFrom, dateTo, rows, outputTax, inputTax, zahllast: +(outputTax - inputTax).toFixed(2) };
}

/** UStVA sonucunu filing olarak saklar (aynı dönem → günceller). */
export async function saveUStVA(result) {
  const client = requireClient();
  const { data: fy } = await client.from('fibu_fiscal_years')
    .select('id').eq('company_id', COMPANY_ID).eq('year', result.year).single();
  if (!fy) throw new Error(`Geschäftsjahr ${result.year} nicht gefunden — zuerst eine Buchung im Jahr erfassen.`);
  const { error } = await client.from('fibu_ustva_filings').upsert({
    company_id: COMPANY_ID, fiscal_year_id: fy.id,
    period_type: 'monthly', period_no: result.month,
    net_vat_due: result.zahllast,
    payload: result,
    status: 'generated',
    generated_at: new Date().toISOString(),
  }, { onConflict: 'company_id,fiscal_year_id,period_type,period_no' });
  if (error) throw error;
}

// ── Mahnwesen ─────────────────────────────────────────────────

export async function listDunningLevels() {
  const client = requireClient();
  const { data, error } = await client.from('fibu_dunning_levels')
    .select('*').eq('company_id', COMPANY_ID).eq('active', true).order('level_no');
  if (error) throw error;
  return data || [];
}

/** Vadesi geçmiş OP için uygun Mahnstufe'yi belirler (gün bazlı). */
export function dueLevel(openItem, levels, today = new Date()) {
  if (!openItem.due_date || openItem.dunning_blocked) return null;
  const daysOverdue = Math.floor((today - new Date(openItem.due_date)) / 86400000);
  if (daysOverdue <= 0) return null;
  const eligible = levels.filter((l) => daysOverdue >= l.days_after_due);
  if (eligible.length === 0) return null;
  const next = eligible[eligible.length - 1];
  return next.level_no > (openItem.dunning_level || 0) ? { ...next, daysOverdue } : null;
}

/** Mahnstufe kaydet (PDF üretimi UI tarafında). */
export async function applyDunning(openItemId, levelNo) {
  const client = requireClient();
  const { error } = await client.from('fibu_open_items')
    .update({ dunning_level: levelNo }).eq('id', openItemId);
  if (error) throw error;
}

// ════════════════════════════════════════════════════════════
// FAZ 3 — Jahresabschluss, ELSTER XML, Kasse/Z-Bon
// ════════════════════════════════════════════════════════════

// ── Jahresabschluss ───────────────────────────────────────────
// Sıra (spesifikasyon §4): prüfen → GuV abschließen → Schlussbilanz
// erzeugen + Jahr sperren → Vortrag als Anfangsbilanz ins Folgejahr.
// Her adım idempotent (source_id / flag korumalı).

const yearRange = (year) => ({ dateFrom: `${year}-01-01`, dateTo: `${year}-12-31` });

async function getFiscalYear(year) {
  const client = requireClient();
  const { data } = await client.from('fibu_fiscal_years')
    .select('*').eq('company_id', COMPANY_ID).eq('year', year).maybeSingle();
  return data;
}

async function hasEntryWithSource(sourceId) {
  const client = requireClient();
  const { data } = await client.from('fibu_journal_entries')
    .select('id').eq('company_id', COMPANY_ID)
    .eq('source_id', sourceId).neq('status', 'reversed').limit(1);
  return (data || []).length > 0;
}

/** Ön kontroller — spesifikasyon §9 Mindestprüfungen'in uygulanabilir kısmı. */
export async function checkYearEnd(year) {
  const client = requireClient();
  const [accounts, totals, fy, openItems] = await Promise.all([
    listAccounts({ activeOnly: false }),
    fetchAccountTotals(yearRange(year)),
    getFiscalYear(year),
    listOpenItems({ type: 'receivable' }),
  ]);

  // Soll = Haben (tüm journal) — RPC garantisi, yine de doğrula
  let sumD = 0, sumC = 0;
  totals.forEach((t) => { sumD += t.debit; sumC += t.credit; });

  // OPOS ↔ 1200 mutabakatı
  const acct1200 = accounts.find((a) => a.account_no === '1200');
  const saldo1200 = acct1200 ? accountBalance(acct1200, totals) : 0;
  const oposSum = openItems.reduce((s, i) => s + Number(i.open_amount), 0);

  const guv = buildGuV(accounts, totals);
  const afaDone = await hasEntryWithSource(`afa:${year}`);
  const guvClosed = await hasEntryWithSource(`guvclose:${year}`);
  const carried = await hasEntryWithSource(`opening:${year + 1}`);

  return {
    fy,
    journalBalanced: Math.abs(sumD - sumC) < 0.01,
    journalDiff: +(sumD - sumC).toFixed(2),
    afaDone,
    oposMatches: Math.abs(saldo1200 - oposSum) < 0.01,
    oposDiff: +(saldo1200 - oposSum).toFixed(2),
    saldo1200, oposSum,
    guvResult: guv.result,
    guvClosed,
    locked: fy?.status === 'locked' || fy?.status === 'closed',
    closingCreated: !!fy?.closing_balance_created,
    carried,
  };
}

/**
 * Adım 1 — GuV-Konten abschließen: tüm GuV hesapları sıfırlanır,
 * net sonuç 7800 Jahresüberschuss/-fehlbetrag'a taşınır. İdempotent.
 */
export async function closeGuV(year, actorEmail) {
  if (await hasEntryWithSource(`guvclose:${year}`)) {
    throw new Error(`GuV-Abschluss ${year} wurde bereits gebucht.`);
  }
  const [accounts, totals] = await Promise.all([
    listAccounts({ activeOnly: false }),
    fetchAccountTotals(yearRange(year)),
  ]);
  const lines = [];
  let result = 0;
  accounts
    .filter((a) => a.statement_type === 'guv')
    .forEach((a) => {
      const bal = accountBalance(a, totals);
      if (Math.abs(bal) < 0.005) return;
      // Konto sıfırlama: normal tarafının TERSİNE yazılır
      const amount = +Math.abs(bal).toFixed(2);
      const zeroSide = (a.normal_side === 'credit') === (bal > 0) ? 'debit' : 'credit';
      lines.push({
        account_no: a.account_no,
        debit: zeroSide === 'debit' ? amount : 0,
        credit: zeroSide === 'credit' ? amount : 0,
        description: `Abschluss ${a.account_no}`,
      });
      result += a.account_type === 'revenue' ? bal : -bal;
    });
  if (lines.length === 0) throw new Error(`Keine GuV-Bewegungen in ${year}.`);

  // Netto sonucu 7800'e: kâr → Haben, zarar → Soll
  const res = +result.toFixed(2);
  lines.push({
    account_no: '7800',
    debit: res < 0 ? Math.abs(res) : 0,
    credit: res > 0 ? res : 0,
    description: `Jahresergebnis ${year}`,
  });

  const entryId = await postEntry({
    bookingDate: `${year}-12-31`,
    description: `GuV-Abschluss ${year} — Ergebnis ${res.toLocaleString('de-DE')} €`,
    source: 'closing',
    sourceId: `guvclose:${year}`,
    lines,
    actorEmail,
  });
  return { entryId, result: res };
}

/**
 * Adım 2 — Schlussbilanz erzeugen + Jahr SPERREN.
 * Bilanz pozisyonları balance_runs.payload'a yazılır; yıl 'locked' olur
 * (RPC bundan sonra bu yıla buchung reddeder — yalnızca Storno mantığı
 * yeni yıl üzerinden işler).
 */
export async function createClosingBalance(year) {
  const client = requireClient();
  const [accounts, totals] = await Promise.all([
    listAccounts({ activeOnly: false }),
    fetchAccountTotals(yearRange(year)),
  ]);
  const positions = accounts
    .filter((a) => a.statement_type === 'balance' || a.statement_type === 'both')
    .map((a) => ({ account_no: a.account_no, name: a.name, account_type: a.account_type, balance: +accountBalance(a, totals).toFixed(2) }))
    .filter((p) => Math.abs(p.balance) >= 0.005);

  let fy = await getFiscalYear(year);
  if (!fy) throw new Error(`Geschäftsjahr ${year} nicht gefunden.`);

  const { error: runErr } = await client.from('fibu_balance_runs').insert({
    company_id: COMPANY_ID, fiscal_year_id: fy.id,
    run_type: 'closing_balance', period_from: 1, period_to: 13,
    payload: { year, positions },
    status: 'locked', locked_at: new Date().toISOString(),
  });
  if (runErr) throw runErr;

  const { error: fyErr } = await client.from('fibu_fiscal_years').update({
    status: 'locked', closing_balance_created: true, locked_at: new Date().toISOString(),
  }).eq('id', fy.id);
  if (fyErr) throw fyErr;

  return { positions, count: positions.length };
}

/**
 * Adım 3 — Vortrag: Schlussbilanz Folgejahr'a Anfangsbilanz olarak
 * taşınır (source 'opening', 01.01). 7800 → 2970 Gewinnvortrag'a devreder.
 * İdempotent (opening:<yıl+1>).
 */
export async function carryForward(year, actorEmail) {
  const client = requireClient();
  const nextYear = year + 1;
  if (await hasEntryWithSource(`opening:${nextYear}`)) {
    throw new Error(`Anfangsbilanz ${nextYear} wurde bereits vorgetragen.`);
  }
  const fy = await getFiscalYear(year);
  if (!fy?.closing_balance_created) {
    throw new Error('Zuerst Schlussbilanz erzeugen (Schritt 2).');
  }
  const { data: runs } = await client.from('fibu_balance_runs')
    .select('payload').eq('company_id', COMPANY_ID)
    .eq('fiscal_year_id', fy.id).eq('run_type', 'closing_balance')
    .order('id', { ascending: false }).limit(1);
  const positions = runs?.[0]?.payload?.positions || [];
  if (positions.length === 0) throw new Error('Schlussbilanz ist leer.');

  const lines = positions.map((p) => {
    // 7800 Jahresergebnis → 2970 Gewinnvortrag olarak devreder
    const account_no = p.account_no === '7800' ? '2970' : p.account_no;
    const isDebitBalance = p.account_type === 'asset';
    const amount = Math.abs(p.balance);
    // Negatif bakiye ters tarafa yazılır
    const debit = (isDebitBalance === (p.balance >= 0)) ? amount : 0;
    return {
      account_no,
      debit: +debit.toFixed(2),
      credit: +(debit ? 0 : amount).toFixed(2),
      description: `EB-Vortrag ${p.name}`,
    };
  });

  const entryId = await postEntry({
    bookingDate: `${nextYear}-01-01`,
    description: `Anfangsbilanz ${nextYear} (Vortrag aus ${year})`,
    source: 'opening',
    sourceId: `opening:${nextYear}`,
    lines,
    actorEmail,
  });

  await client.from('fibu_fiscal_years')
    .update({ opening_balance_created: true }).eq('id', fy.id);
  // Yeni yılın fiscal_year kaydı postEntry içinde otomatik açıldı
  const newFy = await getFiscalYear(nextYear);
  if (newFy) {
    await client.from('fibu_balance_runs').insert({
      company_id: COMPANY_ID, fiscal_year_id: newFy.id,
      run_type: 'opening_balance', payload: { year: nextYear, positions },
      status: 'locked', locked_at: new Date().toISOString(),
    });
  }
  return { entryId, count: lines.length };
}

// ── ELSTER XML ────────────────────────────────────────────────

/**
 * UStVA sonucundan ELSTER-uyumlu Kennzahl XML'i üretir ve filing'e
 * kaydeder. Not: elektronik iletim sertifika gerektirir — bu çıktı
 * Steuerberater/ELSTER-Portal manuel yüklemesi içindir.
 */
export async function generateElsterXml(result) {
  const client = requireClient();
  const period = `${result.year}${String(result.month).padStart(2, '0')}`;
  const kzSums = new Map();
  result.rows.forEach((r) => {
    if (!r.field) return;
    kzSums.set(r.field, +(((kzSums.get(r.field) || 0) + r.amount)).toFixed(2));
  });
  const kzXml = [...kzSums.entries()]
    .map(([kz, val]) => `      <Kz${kz}>${val.toFixed(2).replace('.', ',')}</Kz${kz}>`)
    .join('\n');
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<Anmeldungssteuern art="UStVA" version="2026">
  <Erstellungsdatum>${new Date().toISOString().slice(0, 10)}</Erstellungsdatum>
  <Steuerfall>
    <Umsatzsteuervoranmeldung>
      <Jahr>${result.year}</Jahr>
      <Zeitraum>${String(result.month).padStart(2, '0')}</Zeitraum>
${kzXml}
      <Kz83>${result.zahllast.toFixed(2).replace('.', ',')}</Kz83>
    </Umsatzsteuervoranmeldung>
  </Steuerfall>
</Anmeldungssteuern>`;

  // Filing'e yaz (varsa)
  const fy = await getFiscalYear(result.year);
  if (fy) {
    await client.from('fibu_ustva_filings').update({ elster_xml: xml })
      .eq('company_id', COMPANY_ID).eq('fiscal_year_id', fy.id)
      .eq('period_type', 'monthly').eq('period_no', result.month);
  }

  // Tarayıcıda indir
  const blob = new Blob([xml], { type: 'application/xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `UStVA_${period}.xml`;
  a.click();
  URL.revokeObjectURL(url);
  return xml;
}

// ── Kasse / Z-Bon ─────────────────────────────────────────────

/**
 * Z-Bon (Tagesabschluss) buchar: Kasse an Erlöse 19%/7% + USt.
 * Transaksiyon kaydı 'matched' olarak eklenir (kaynak: cash).
 * İdempotent: aynı bağlantı+gün ikinci kez buchulamaz (UNIQUE ext id).
 */
export async function bookZBon({ connectionId, date, gross19 = 0, gross7 = 0, actorEmail }) {
  const client = requireClient();
  const g19 = +Number(gross19 || 0).toFixed(2);
  const g7 = +Number(gross7 || 0).toFixed(2);
  const total = +(g19 + g7).toFixed(2);
  if (total <= 0) throw new Error('Z-Bon: Betrag muss größer 0 sein.');

  const net19 = +(g19 / 1.19).toFixed(2);
  const tax19 = +(g19 - net19).toFixed(2);
  const net7 = +(g7 / 1.07).toFixed(2);
  const tax7 = +(g7 - net7).toFixed(2);

  const lines = [{ account_no: '1600', debit: total, credit: 0, description: `Z-Bon ${date}` }];
  if (net19 > 0) {
    lines.push({ account_no: '4400', debit: 0, credit: net19, tax_code: 'USt19', description: 'Barerlöse 19%' });
    lines.push({ account_no: '3806', debit: 0, credit: tax19, description: 'USt 19%' });
  }
  if (net7 > 0) {
    lines.push({ account_no: '4401', debit: 0, credit: net7, tax_code: 'USt7', description: 'Barerlöse 7%' });
    lines.push({ account_no: '3730', debit: 0, credit: tax7, description: 'USt 7%' });
  }
  // Yuvarlama farkını ilk erlös satırına düzelt (Soll=Haben garantisi)
  const sumCredit = lines.slice(1).reduce((s, l) => s + l.credit, 0);
  const diff = +(total - sumCredit).toFixed(2);
  if (Math.abs(diff) >= 0.01) lines[1].credit = +(lines[1].credit + diff).toFixed(2);

  const { error: txErr } = await client.from('fibu_financial_transactions').insert({
    connection_id: connectionId,
    external_transaction_id: `zbon:${date}`,
    booking_date: date,
    amount: total,
    purpose: `Z-Bon Tagesabschluss ${date}`,
    reconciliation_status: 'matched',
  });
  if (txErr) {
    if ((txErr.message || '').includes('duplicate')) {
      throw new Error(`Z-Bon für ${date} wurde bereits erfasst.`);
    }
    throw txErr;
  }

  return postEntry({
    bookingDate: date,
    description: `Z-Bon Tagesabschluss ${date}`,
    source: 'cash',
    sourceId: `zbon:${date}`,
    lines,
    actorEmail,
  });
}

// ══════════════════════════════════════════════════════════════
// MAHNWESEN — otomatik kademe akışı (spesifikasyon):
//   Stufe 0  Zahlungserinnerung  ücretsiz          vade +3 gün
//   Stufe 1  1. Mahnung          %8 Gebühr (netto) vade +7 gün
//   Stufe 2  2. Mahnung          tekrar %8         vade +7 gün
//   Stufe 3  Inkasso-Übergabe    (manuel onay, tüm gebühren dahil)
// Idempotens: UNIQUE(open_item_id, level_no) DB'de garantili.
// ══════════════════════════════════════════════════════════════

export const MAHN_LEVELS = {
  0: { name: 'Zahlungserinnerung', feeRate: 0,    days: 3 },
  1: { name: '1. Mahnung',         feeRate: 0.08, days: 7 },
  2: { name: '2. Mahnung',         feeRate: 0.08, days: 7 },
  3: { name: 'Inkasso',            feeRate: 0,    days: 0 },
};

function addDays(dateStr, days) {
  const d = new Date(dateStr || new Date().toISOString().slice(0, 10));
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

async function insertMahnung({ openItem, levelNo, prevTotalFees, actorEmail }) {
  const client = requireClient();
  const cfg = MAHN_LEVELS[levelNo];
  const gross = Number(openItem.open_amount) || 0;
  const net = +(gross / 1.19).toFixed(2);
  const fee = +(net * cfg.feeRate).toFixed(2);
  const totalFees = +(prevTotalFees + fee).toFixed(2);

  const { data, error } = await client.from('fibu_mahnungen').insert({
    company_id: COMPANY_ID,
    open_item_id: openItem.id,
    invoice_no: openItem.invoice_no,
    partner_name: openItem.partner_name,
    partner_email: openItem.partner_email || null,
    level_no: levelNo,
    level_name: cfg.name,
    net_amount: net,
    fee_amount: fee,
    total_fees: totalFees,
    open_amount: gross,
    total_due: +(gross + totalFees).toFixed(2),
    due_date: cfg.days > 0 ? addDays(null, cfg.days) : null,
    created_by: actorEmail || null,
  }).select().single();
  if (error) {
    if ((error.message || '').includes('duplicate')) return null; // idempotent
    throw error;
  }
  await client.from('fibu_open_items')
    .update({ dunning_level: levelNo }).eq('id', openItem.id);
  return data;
}

/**
 * Otomatik Mahnlauf: vadesi geçmiş tüm açık faturaları tarar ve sıradaki
 * kademeyi OLUŞTURUR (Stufe 0→1→2 otomatik; Inkasso manuel).
 * Sayfa açılışında çağrılır — idempotent, iki kez koşmak güvenlidir.
 * @returns {{created: Array, inkassoReady: Array}}
 */
export async function runMahnlauf(actorEmail) {
  const client = requireClient();
  const today = new Date().toISOString().slice(0, 10);

  const { data: items, error } = await client.from('fibu_open_items')
    .select('*')
    .eq('company_id', COMPANY_ID)
    .eq('type', 'receivable')
    .in('status', ['open', 'partial'])
    .eq('dunning_blocked', false)
    .is('inkasso_at', null);
  if (error) throw error;

  const { data: existing } = await client.from('fibu_mahnungen')
    .select('open_item_id, level_no, due_date, storniert')
    .eq('company_id', COMPANY_ID);
  const byItem = new Map();
  (existing || []).forEach((m) => {
    if (m.storniert) return;
    const list = byItem.get(m.open_item_id) || [];
    list.push(m);
    byItem.set(m.open_item_id, list);
  });

  const created = [];
  const inkassoReady = [];

  for (const item of items || []) {
    if (!item.due_date || item.due_date >= today) continue; // fatura vadesi geçmemiş

    const levels = (byItem.get(item.id) || []).sort((a, b) => b.level_no - a.level_no);
    const last = levels[0] || null;
    const prevFees = levels.reduce((s, m) => Math.max(s, 0), 0); // total_fees seçilmedi → tekrar hesapla
    const cumFees = (lvl) => {
      const net = (Number(item.open_amount) || 0) / 1.19;
      let sum = 0;
      for (let i = 1; i <= lvl; i++) sum += net * (MAHN_LEVELS[i]?.feeRate || 0);
      return +sum.toFixed(2);
    };

    if (!last) {
      // Fatura vadesi geçti → Zahlungserinnerung
      const rec = await insertMahnung({ openItem: item, levelNo: 0, prevTotalFees: 0, actorEmail });
      if (rec) created.push(rec);
    } else if (last.level_no < 2 && last.due_date && last.due_date < today) {
      // Önceki kademenin vadesi de geçti → sıradaki Mahnung
      const nextLevel = last.level_no + 1;
      const rec = await insertMahnung({
        openItem: item, levelNo: nextLevel,
        prevTotalFees: cumFees(nextLevel - 1),
        actorEmail,
      });
      if (rec) created.push(rec);
    } else if (last.level_no === 2 && last.due_date && last.due_date < today) {
      // 2. Mahnung vadesi geçti → Inkasso'ya hazır (manuel onay)
      inkassoReady.push(item.id);
    }
  }

  return { created, inkassoReady };
}

/** Inkasso-Übergabe: kayıt oluşturur + OP işaretlenir (manuel onaylı). */
export async function uebergabeInkasso(openItemId, actorEmail) {
  const client = requireClient();
  const { data: item, error } = await client.from('fibu_open_items')
    .select('*').eq('id', openItemId).single();
  if (error) throw error;

  const net = +((Number(item.open_amount) || 0) / 1.19).toFixed(2);
  const totalFees = +(net * 0.08 * 2).toFixed(2); // 1.+2. Mahnung gebühren
  const rec = await insertMahnung({
    openItem: item, levelNo: 3, prevTotalFees: totalFees, actorEmail,
  });
  await client.from('fibu_open_items')
    .update({ inkasso_at: new Date().toISOString(), status: 'open' })
    .eq('id', openItemId);
  return rec;
}

/** Liste — durum OPOS'tan canlı: bezahlt/faellig/offen/storniert/inkasso */
export async function listMahnungen() {
  const client = requireClient();
  const { data, error } = await client.from('fibu_mahnungen')
    .select('*, fibu_open_items(status, open_amount, due_date, inkasso_at)')
    .eq('company_id', COMPANY_ID)
    .order('created_at', { ascending: false });
  if (error) throw error;

  const today = new Date().toISOString().slice(0, 10);
  return (data || []).map((m) => {
    let status = 'offen';
    if (m.storniert) status = 'storniert';
    else if (m.level_no === 3) status = 'inkasso';
    else if (m.fibu_open_items?.status === 'paid') status = 'bezahlt';
    else if (m.due_date && m.due_date < today) status = 'faellig';
    return {
      ...m,
      status,
      current_open: m.fibu_open_items?.status === 'paid'
        ? 0
        : +(Number(m.fibu_open_items?.open_amount ?? m.open_amount) + Number(m.total_fees)).toFixed(2),
    };
  });
}

export async function stornoMahnung(id) {
  const client = requireClient();
  const { error } = await client.from('fibu_mahnungen')
    .update({ storniert: true }).eq('id', id);
  if (error) throw error;
}

/** E-posta gönderimini kayda işler. */
export async function markMahnungEmailSent(id, to) {
  const client = requireClient();
  const { error } = await client.from('fibu_mahnungen')
    .update({ email_sent_at: new Date().toISOString(), email_sent_to: to })
    .eq('id', id);
  if (error) throw error;
}
